import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="date-range-picker"
export default class extends Controller {
    static values = { range: Boolean, clearable: Boolean }
    static targets = ["clearButton"]

    connect() {
        this.initializeDateRangePicker()
        this.toggleClearButton()
        this.attachClearButtonHandler()

        const inputGroup = this.element.closest('.input-group')
        if (inputGroup) {
            const calendarIcon = inputGroup.querySelector('.input-group-text')
            if (calendarIcon) {
                calendarIcon.addEventListener('click', (e) => {
                    e.preventDefault()
                    $(this.element).data('daterangepicker').show()
                })
            }
        }
    }

    disconnect() {
        if (this.picker) {
            this.picker.remove()
        }
    }

    initializeDateRangePicker() {
        const isRange = this.rangeValue
        const locale = {
            "format": "DD.MM.YYYY HH:mm",
            "separator": " - ",
            "applyLabel": "Применить",
            "cancelLabel": "Отмена",
            "fromLabel": "От",
            "toLabel": "До",
            "customRangeLabel": "Настроить",
            "weekLabel": "Н",
            "daysOfWeek": ["Вс", "Пн", "Вт", "Ср", "Чт", "Пт", "Сб"],
            "monthNames": [
                "Январь", "Февраль", "Март", "Апрель", "Май", "Июнь",
                "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь"
            ],
            "firstDay": 1
        }

        const config = {
            locale: locale,
            autoUpdateInput: false,
            showDropdowns: true,
            timePicker: true,
            timePicker24Hour: true,
            timePickerIncrement: 1,
            minDate: moment(),
            startDate: moment(),
            endDate: moment().add(1, 'hours')
        }

        if (!isRange) {
            config.singleDatePicker = true
        }

        // Инициализируем daterangepicker
        this.picker = $(this.element).daterangepicker(config)

        // Обработчики событий
        $(this.element).on('apply.daterangepicker', (ev, picker) => {
            if (isRange) {
                const start = picker.startDate.format('DD.MM.YYYY HH:mm')
                const end = picker.endDate.format('DD.MM.YYYY HH:mm')
                this.element.value = `${start} - ${end}`
            } else {
                // Для одиночной даты показываем пользователю читаемый формат
                const selectedDate = picker.startDate
                this.element.value = selectedDate.format('DD.MM.YYYY HH:mm')

                // Создаем скрытое поле с техническим форматом для Rails
                this.updateHiddenFieldForSingleDate(selectedDate)
            }

            // Показываем/скрываем кнопку очистки
            this.toggleClearButton()

            // Триггерим событие change для обновления формы
            this.element.dispatchEvent(new Event('change', { bubbles: true }))
        })

        $(this.element).on('cancel.daterangepicker', (ev, picker) => {
            this.element.value = ''

            // Очищаем скрытое поле тоже
            if (!isRange) {
                const originalName = this.element.name.replace('_display', '')
                const hiddenField = document.querySelector(`input[name="${originalName}"][type="hidden"]`)
                if (hiddenField) {
                    hiddenField.value = ''
                }
            }

            this.toggleClearButton()
            this.element.dispatchEvent(new Event('change', { bubbles: true }))
        })

        // Если есть начальное значение, устанавливаем его
        if (this.element.value) {
            this.setInitialValue()
        }
    }

    updateHiddenField(momentDate) {
        // Создаем или обновляем скрытое поле для правильной передачи в Rails
        const fieldName = this.element.name
        let hiddenField = document.querySelector(`input[name="${fieldName}"][type="hidden"]`)

        if (!hiddenField) {
            hiddenField = document.createElement('input')
            hiddenField.type = 'hidden'
            hiddenField.name = fieldName
            this.element.parentNode.appendChild(hiddenField)
        }

        // Форматируем дату для Rails (ISO 8601)
        hiddenField.value = momentDate.format('YYYY-MM-DD HH:mm:ss')
    }

    updateHiddenFieldForSingleDate(momentDate) {
        // Для одиночной даты не создаем скрытое поле, а изменяем name основного поля
        // чтобы отправлять техническое значение
        const originalName = this.element.name
        const hiddenFieldName = originalName + '_hidden'

        // Находим или создаем скрытое поле
        let hiddenField = document.querySelector(`input[name="${originalName}"][type="hidden"]`)

        if (!hiddenField) {
            hiddenField = document.createElement('input')
            hiddenField.type = 'hidden'
            hiddenField.name = originalName
            this.element.parentNode.appendChild(hiddenField)

            // Меняем name у основного поля, чтобы оно не отправлялось
            this.element.name = originalName + '_display'
        }

        // Форматируем дату для Rails
        hiddenField.value = momentDate.format('YYYY-MM-DD HH:mm:ss')
    }

    setInitialValue() {
        const value = this.element.value.trim()
        if (!value) return

        const isRange = this.rangeValue

        try {
            if (isRange && value.includes(' - ')) {
                const [startStr, endStr] = value.split(' - ')
                const startDate = moment(startStr, 'DD.MM.YYYY HH:mm')
                const endDate = moment(endStr, 'DD.MM.YYYY HH:mm')

                if (startDate.isValid() && endDate.isValid()) {
                    this.picker.data('daterangepicker').setStartDate(startDate)
                    this.picker.data('daterangepicker').setEndDate(endDate)
                }
            } else if (!isRange) {
                // Пробуем разные форматы даты
                let date = moment(value, 'DD.MM.YYYY HH:mm')

                if (!date.isValid()) {
                    // Если не получилось, пробуем ISO формат
                    date = moment(value)
                }

                if (date.isValid()) {
                    this.picker.data('daterangepicker').setStartDate(date)
                    this.picker.data('daterangepicker').setEndDate(date)
                    // Обновляем отображаемое значение в правильном формате
                    this.element.value = date.format('DD.MM.YYYY HH:mm')
                }
            }
        } catch (error) {
            console.error('Ошибка при установке начального значения daterangepicker:', error)
        }
    }

    // Метод для очистки даты
    clearDate() {
        console.log('Clearing date')
        this.element.value = ''

        // Очищаем скрытое поле тоже
        const originalName = this.element.name.replace('_display', '')
        const hiddenField = document.querySelector(`input[name="${originalName}"][type="hidden"]`)
        if (hiddenField) {
            hiddenField.value = ''
        }

        this.toggleClearButton()
        this.element.dispatchEvent(new Event('change', { bubbles: true }))
    }

    // Метод для показа/скрытия кнопки очистки
    toggleClearButton() {
        console.log('Toggling clear button visibility', this.clearableValue)
        if (!this.clearableValue) return

        const clearButton = this.element.parentNode.querySelector('[data-action*="clearDate"]')
        if (clearButton) {
            if (this.element.value && this.element.value.trim() !== '') {
                clearButton.classList.remove('d-none')
            } else {
                clearButton.classList.add('d-none')
            }
        }
    }

    attachClearButtonHandler() {
        // Находим кнопку очистки внутри input-group
        const clearButton = this.element.parentNode.querySelector('[data-action*="clearDate"]')
        if (clearButton) {
            clearButton.removeEventListener('click', this._clearDateHandler)
            this._clearDateHandler = this.clearDate.bind(this)
            clearButton.addEventListener('click', this._clearDateHandler)
        }
    }
}
